/**
 * The presentation model of the machine identity pages (plan 2026-10-21-release-on-zion-build,
 * WS-17, R-042 item 1, R-044 item 2).
 *
 * Pure functions and total records only, so each branch has a test with no render. The list page
 * (`MachineIdentitiesPane.tsx`), the detail page (`MachineIdentityPane.tsx`) and the dialogs
 * (`machine-identity-dialogs.tsx`) read their copy, their guards and their wire bodies from here.
 *
 * The contract module `machine-identity-admin.ts` of `@zion/sdk-contracts` is the one authority on
 * what the server accepts. Each draft check below parses with a contract schema and adds only the
 * sentence an operator reads. No grammar of the contract is copied here.
 */
import {
  CP_MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH,
  CP_MACHINE_PRINCIPAL_SLUG_MAX_LENGTH,
  CP_MACHINE_PRINCIPAL_SLUG_MIN_LENGTH,
  CP_MACHINE_RESOURCE_GRANT_ID_MAX_LENGTH,
  CP_MACHINE_RESOURCE_GRANT_ROLE_SLUG_MAX_LENGTH,
  CP_MACHINE_SITE_PERMISSIONS,
  CP_MACHINE_TRUST_POLICY_REF_PREFIX_MAX_LENGTH,
  FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS,
  FEDERATED_TOKEN_MAX_TTL_SECONDS,
  GRANT_RESOURCE_TYPES,
  PLATFORM_RESOURCE_ID,
  SUBMITTER_KIND_CLAIM,
  cpMachinePrincipalSlugSchema,
  cpMachineTrustPolicyIssuerSchema,
  cpMachineTrustPolicyRefPrefixSchema,
  cpMachineTrustPolicyRequiredClaimsSchema,
  cpMachineTrustPolicySubjectSchema,
  createCpMachineTrustPolicyRequestSchema,
  type CpMachinePrincipal,
  type CpMachineResourceGrantRow,
  type CpMachineSiteGrant,
  type CpMachineSitePermission,
  type CpMachineTrustPolicy,
  type CreateCpMachinePrincipalRequest,
  type CreateCpMachineResourceGrantRequest,
  type CreateCpMachineTrustPolicyRequest,
  type GrantResourceType,
} from '@zion/sdk-contracts';
import { displayMessage } from '../platform/errors';
import { ApiError } from '../platform/http/client';
import {
  REDACTED_TEXT,
  UNTRUSTED_BLOCK_MAX_CHARS,
  UNTRUSTED_TEXT_MAX_CHARS,
  displayUntrusted,
  displayUntrustedName,
  isBlankText,
} from '../platform/untrusted-text';

// ── Display of untrusted text ────────────────────────────────────────────────────────────────────

/*
 * One separator that can sit between two digits of a credential and split it for the eye: an
 * escape token `<U+XXXX>` of `displayUntrusted`, or one code point of its escape class (a control,
 * a format character such as U+200B, U+200C or U+200D, a line or paragraph separator, a blank-looking
 * filler), or a combining mark. Tab, line feed and carriage return are no separator here: the layout
 * step of `displayUntrusted` shows each as a space, as a typed space shows.
 */
const SEPARATOR = `(?:<U\\+[0-9A-F]{4,6}>|(?![\\t\\n\\r])[\\p{Cc}\\p{Cf}\\p{Mn}\\p{Zl}\\p{Zp}${String.fromCharCode(0x115f, 0x1160, 0x3164, 0xffa0, 0x2800)}])`;

/*
 * A run of 64 or more hex digits, with any number of {@link SEPARATOR}s between two digits, in any
 * place: after `_`, `-`, a letter, a digit or `sha256:` too. A CP session token has this shape. The
 * opaque-token rule P11 of `untrusted-text.ts` keeps it, so a git sha and an image digest stay
 * readable on other pages. A machine identity string has no reason to hold such a run, so this page
 * hides it, also when a separator splits it or a word touches it.
 */
const HEX_TOKEN_RUN = new RegExp(`[0-9A-Fa-f](?:${SEPARATOR}*[0-9A-Fa-f]){63,}`, 'gu');

/*
 * A run of two or more hex groups joined by `-` or `_`, with no token character on either side: the
 * shape of a lower-case kebab or snake credential (`a1b2-c3d4-...`), which P11 keeps as a joined
 * identifier. {@link isJoinedHexSecret} decides on each match.
 */
const JOINED_HEX_RUN = /(?<![A-Za-z0-9_-])[0-9A-Fa-f]+(?:[-_][0-9A-Fa-f]+)+(?![A-Za-z0-9_-])/g;

/* The fewest hex digits of a joined run that this page hides: the 32 digits of a UUID. */
const JOINED_HEX_MIN_DIGITS = 32;

/* A UUID in either case, the whole run: every id of this page has this shape and stays readable. */
const UUID_RUN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* `true` for a joined hex run of 32 or more digits that is not a UUID. Pure. */
function isJoinedHexSecret(run: string): boolean {
  return !UUID_RUN.test(run) && run.replace(/[-_]/g, '').length >= JOINED_HEX_MIN_DIGITS;
}

/*
 * The text with each {@link HEX_TOKEN_RUN} and each joined hex secret ({@link JOINED_HEX_RUN},
 * {@link isJoinedHexSecret}) as `[redacted]`. It reads raw text and the output of
 * `displayUntrusted` alike, because a separator may be a code point or its escape token. Pure, and
 * a second pass changes nothing.
 */
function redactMachineSecrets(text: string): string {
  return text
    .replace(HEX_TOKEN_RUN, REDACTED_TEXT)
    .replace(JOINED_HEX_RUN, (run) => (isJoinedHexSecret(run) ? REDACTED_TEXT : run));
}

/* U+200C and U+200D, the two joiners that `displayUntrustedName` keeps in a name (R-8 of plan G8). */
const NAME_JOINERS = new RegExp(`([${String.fromCharCode(0x200c, 0x200d)}])`);

/**
 * Show one machine identity string that the console did not write and that is not a name: an
 * issuer, a subject, a claim line, a ref prefix, a resource id, a role slug, a slug, or the text of
 * a refusal. The steps:
 *
 * 1. {@link redactMachineSecrets} on the raw text, before the cap. So a hex run that a separator
 *    splits, or that the cap would cut below 64 digits, goes whole.
 * 2. `displayUntrusted` with the cap {@link UNTRUSTED_TEXT_MAX_CHARS}: cap, one line, escape, and
 *    the secret shapes of its rules.
 * 3. {@link redactMachineSecrets} again on the escaped text.
 *
 * Callers: the two machine identity pages, their dialogs, and the functions of this module that
 * build a cell, a notice or a failure line.
 *
 * Input: any string. Output: one line of at most 4000 code points and the cut marker when cut. The
 * cap is above every bound of the contract (a subject of 512, claims of 1024 JSON characters, a
 * resource id of 256), so a legal value shows whole and two values that differ late never look the
 * same. A UUID and a hex run of fewer than 64 digits with no joiner, such as a git sha, stay. Pure;
 * a second pass over output that fits changes nothing.
 */
export function displayMachineText(text: string): string {
  return redactMachineSecrets(displayUntrusted(redactMachineSecrets(text), { maxChars: UNTRUSTED_TEXT_MAX_CHARS }));
}

/**
 * Show one machine identity name that the console did not write: a display name, or a notice that
 * holds one. The steps:
 *
 * 1. {@link redactMachineSecrets} on the raw text. A hex run that U+200C or U+200D splits goes whole
 *    here, before the joiners split the name.
 * 2. `displayUntrustedName`: cap, one line, escape, with U+200C and U+200D kept.
 * 3. The redaction of `displayUntrusted` and {@link redactMachineSecrets} on each part between two
 *    joiners, then {@link redactMachineSecrets} on the whole line.
 *
 * So a name in a script that needs a joiner reads as typed, and a credential in a name shows as
 * `[redacted]`.
 *
 * Input: any string, and `maxChars`, a positive integer, default 256. Output: one line of at most
 * `maxChars` code points and the cut marker when cut. Errors: a `maxChars` that is not a positive
 * integer throws the `RangeError` of `displayUntrustedName`. Pure; a second pass over output that
 * fits changes nothing.
 */
export function displayMachineName(text: string, maxChars?: number): string {
  return redactMachineSecrets(
    displayUntrustedName(redactMachineSecrets(text), maxChars === undefined ? {} : { maxChars })
      .split(NAME_JOINERS)
      .map((part, index) => (index % 2 === 1 ? part : redactMachineSecrets(displayUntrusted(part, { maxChars: UNTRUSTED_BLOCK_MAX_CHARS }))))
      .join(''),
  );
}

/**
 * The visible name of a principal: its display name through {@link displayMachineName}, or its slug
 * through {@link displayMachineText} when the display name shows no ink. The contract admits a
 * display name of spaces, line feeds, U+200B, U+200C or U+200D alone (`min(1)` with no trim), and a
 * link, a heading or a title with no visible text names no principal.
 *
 * Callers: the list link, the detail heading, the dialog titles, the table captions and the notices.
 * Input: a principal or a list row. Output: one line. Pure.
 */
export function principalLabel(principal: Pick<CpMachinePrincipal, 'displayName' | 'slug'>): string {
  return isBlankText(principal.displayName) ? displayMachineText(principal.slug) : displayMachineName(principal.displayName);
}

/**
 * The text the operator types to disable a principal: the slug when {@link displayMachineText}
 * shows it as it stands, else the principal id. The typed gate of `ConfirmDialog` shows its expected
 * text, so a slug that holds a credential (a 64-digit hex run, a joined hex secret) would show in
 * full there. The id is a UUID, which every redaction keeps. Pure.
 */
export function disableConfirmationText(principal: Pick<CpMachinePrincipal, 'id' | 'slug'>): string {
  return displayMachineText(principal.slug) === principal.slug ? principal.slug : principal.id;
}

// ── Routes ───────────────────────────────────────────────────────────────────────────────────────

/** The console route of the list page. The manifest entry `settings-machine-identities` names it. */
export const MACHINE_IDENTITIES_ROUTE = '/settings/machine-identities';

/** The route parameter of the detail page: `${MACHINE_IDENTITIES_ROUTE}/:principalId`. */
export const MACHINE_IDENTITY_PARAM = 'principalId';

/** The console route of one principal's detail page. The id is percent-encoded. Pure. */
export function machineIdentityRoute(principalId: string): string {
  return `${MACHINE_IDENTITIES_ROUTE}/${encodeURIComponent(principalId)}`;
}

/**
 * The notice that the list page hands to the detail page in the router state after a create:
 * `state.notice` through {@link displayMachineName} with the cap {@link UNTRUSTED_TEXT_MAX_CHARS},
 * when it is a non-empty string, else `null`. Any other state, `null` included, gives `null`, so a
 * state that another page left behind shows nothing. The router state is browser history that any
 * page can write, so the notice passes the redaction here and not only where the list built it.
 * Pure.
 */
export function noticeFromRouteState(state: unknown): string | null {
  if (state === null || typeof state !== 'object' || !('notice' in state)) return null;
  const notice = state.notice;
  return typeof notice === 'string' && notice !== '' ? displayMachineName(notice, UNTRUSTED_TEXT_MAX_CHARS) : null;
}

/**
 * The `sub` of every token the principal mints: `machine:<id>`. The registry door and the audit
 * trail name the principal by it, so the detail page shows it. Pure.
 */
export function machineSubject(principalId: string): string {
  return `machine:${principalId}`;
}

// ── Status ───────────────────────────────────────────────────────────────────────────────────────

/** The word of a Status badge of a principal or a policy: `Enabled` or `Disabled`. Pure. */
export function enabledLabel(disabled: boolean): string {
  return disabled ? 'Disabled' : 'Enabled';
}

/** The tone of that badge: neutral while enabled, warning when disabled. Pure. */
export function enabledTone(disabled: boolean): 'neutral' | 'warning' {
  return disabled ? 'warning' : 'neutral';
}

// ── Copy ─────────────────────────────────────────────────────────────────────────────────────────

/** The list page subtitle: what a machine identity is, and who uses this page. */
export const MACHINE_IDENTITIES_SUBTITLE =
  'Non-human callers, such as the jobs of a release workflow, that exchange an OIDC token for a control plane token. A trust policy says which token a machine identity accepts; its grants say what the token may do.';

/** Why Add trust policy holds on a disabled principal: it mints no token, so a policy matches nothing. */
export const POLICY_DISABLED_PRINCIPAL_REASON = 'A disabled machine identity mints no token, so a new policy would match nothing.';

/** The intro of the create dialog: a new principal can do nothing until it has a policy and a grant. */
export const CREATE_PRINCIPAL_INTRO =
  'A new machine identity has no trust policy and no grant, so it cannot sign in and can do nothing. Add a trust policy and grants on its page.';

/**
 * What a principal disable does. `CpTokenMinter` gives an unscoped token at most 900 s
 * (`CP_FEDERATED_TOKEN_TTL`), a release-source scoped token at most 3,600 s
 * (`CP_FEDERATED_RELEASE_TOKEN_TTL`), and a token of an unscoped policy with a `tokenTtlSeconds`
 * value at most that value, 3,600 s or less (plan 2026-10-31-door-long-push, D1). A token keeps the
 * `exp` of its mint: a later change of the policy value moves no earlier token
 * (`MachinePrincipalService.setTrustPolicyTokenTtl`). So the copy ties the one-hour bound to the
 * policy value at the mint, and the policy row of today does not show it. Each bound has its own
 * sentence and its own token class, so no sentence gives two bounds for one token.
 */
export const DISABLE_PRINCIPAL_CONSEQUENCE =
  'The control plane refuses every new token exchange for this machine identity. A token it received before keeps working until it expires. A token from a release-source scoped policy, or from a policy that had its own token lifetime at the mint, keeps working for at most one hour. Any other token keeps working for at most 15 minutes. No control on this page enables it again.';

/**
 * What a trust policy disable does. A token minted under the policy keeps the `exp` of its mint.
 * The Token lifetime cell of the policy row ({@link tokenLifetimeLabel}) shows the value of today,
 * and a change of that value moves no earlier token (`MachinePrincipalService.setTrustPolicyTokenTtl`).
 * A policy that held 3,600 s and now holds 60 s can still have a token of one hour live. So the copy
 * names the one bound that holds for every policy and every history: one hour, the 3,600 s maximum of
 * a policy value and of `CP_FEDERATED_RELEASE_TOKEN_TTL` (plan 2026-10-31-door-long-push, D1, D3).
 */
export const DISABLE_POLICY_CONSEQUENCE =
  'This policy matches no new token exchange. A token minted under it keeps working until it expires, at most one hour. A change of the token lifetime of this policy does not shorten a token minted before the change. To use the same policy again, a new policy with the same issuer, subject and claims is refused: the match key spans disabled policies.';

/**
 * What a site grant does, shown in the add dialog. `CpTokenMinter` puts the site permissions into a
 * token only under an unscoped policy with a ref prefix, and only when the verified `ref` claim
 * starts with that prefix and has more after it (`refHasRequiredPrefix`).
 */
export const SITE_GRANT_CONSEQUENCE =
  'A site grant gives the permission on every resource of the control plane. A token of this machine identity carries it only under an unscoped trust policy with a ref prefix, when the verified ref claim starts with that prefix.';

/**
 * What a site grant removal does. Only an unscoped token carries a site permission. It lives at most
 * 900 s, or at most the `tokenTtlSeconds` value that its policy held at the mint, 3,600 s or less
 * (plan 2026-10-31-door-long-push, D1). A later change of the policy value moves no earlier token.
 * Each bound has its own sentence and its own token class, as in {@link DISABLE_PRINCIPAL_CONSEQUENCE}.
 */
export const REMOVE_SITE_GRANT_CONSEQUENCE =
  'New tokens of this machine identity no longer carry the permission. A token minted before keeps it until it expires. A token from a policy that had its own token lifetime at the mint keeps it for at most one hour. Any other token keeps it for at most 15 minutes.';

/**
 * What a resource grant revoke does. The control plane stamps the grant revoked and keeps the row,
 * and the grant list read answers active grants alone, so the row leaves this card.
 */
export const REVOKE_RESOURCE_GRANT_CONSEQUENCE =
  'The machine identity loses this role on this resource. The grant leaves this list; the control plane keeps it in its record as revoked.';

/** Why Add site grant holds on a disabled principal: the route answers 409. */
export const SITE_GRANT_DISABLED_REASON = 'A disabled machine identity takes no new site grant.';

/** Why the write controls of the Identity and Trust policies cards hold while the detail reads again. */
export const DETAIL_REREAD_REASON = 'Reading the machine identity again.';

/**
 * Why those controls hold after a failed re-read: the cards show the last answer, which can be
 * stale, so no write acts on it.
 */
export const DETAIL_STALE_REASON = 'The last read failed. Try again before you change this machine identity.';

/** Why the controls of the Site grants card hold while its list reads again after a write. */
export const SITE_GRANT_REREAD_REASON = 'Reading the site grants again.';

/** Why the controls of the Resource grants card hold while its list reads again after a write. */
export const RESOURCE_GRANT_REREAD_REASON = 'Reading the resource grants again.';

/**
 * The failure line of a write whose answer did not arrive or did not fit the contract
 * (`isUncertainWriteFailure`). The control plane may have made the change, so the dialog holds its
 * submit and the page reads again.
 */
export const UNCERTAIN_WRITE_LINE =
  'No usable answer arrived, so the control plane may have made this change. The page reads it again. Cancel, then check the page before you try again.';

/** Why the submit of a dialog holds after an uncertain write. */
export const UNCERTAIN_WRITE_REASON = 'Cancel and check the page first: the last attempt may have made this change.';

/** Why Add site grant holds when the principal already holds every permission of the closed list. */
export const SITE_GRANT_COMPLETE_REASON = 'This machine identity holds every site permission a machine may hold.';

/** The hint under the release-source scoped choice of the trust policy dialog. */
export const RELEASE_SOURCE_SCOPED_HINT =
  'A release-source scoped policy does not compare the subject. The release sources of an app then name the one app the token may publish. It must require submitter_kind = human or agent.';

/**
 * The hint under the ref prefix field. The minter puts the site grants into a token only when its
 * policy has a prefix and the verified `ref` claim starts with it; the trust policy pipe of
 * cp-auth-api admits only `refs/heads/release/` and prefixes under it (WS-15b).
 */
export const REF_PREFIX_HINT =
  "The tokens of this policy carry the site grants only when the verified ref claim starts with this prefix and has more after it. Use refs/heads/release/ or a prefix under it. Such a policy must require submitter_kind = human and yaml_job_id. Leave it empty for a policy whose tokens carry no site grant; a machine identity that holds site grants takes no such policy.";

// ── Principal create ─────────────────────────────────────────────────────────────────────────────

/** The two fields of the create dialog, as the operator typed them. */
export interface PrincipalDraft {
  readonly slug: string;
  readonly displayName: string;
}

/** The draft the create dialog opens with: two empty fields. */
export const EMPTY_PRINCIPAL_DRAFT: PrincipalDraft = { slug: '', displayName: '' };

/**
 * The problem of the Slug field, or `null`. An empty slug is no field problem: the blocked reason
 * names it. A slug that `cpMachinePrincipalSlugSchema` refuses after a trim gets one sentence with
 * the grammar and the bounds. Pure.
 */
export function principalSlugProblem(slug: string): string | null {
  const value = slug.trim();
  if (value === '' || cpMachinePrincipalSlugSchema.safeParse(value).success) return null;
  return `Use ${CP_MACHINE_PRINCIPAL_SLUG_MIN_LENGTH} to ${CP_MACHINE_PRINCIPAL_SLUG_MAX_LENGTH} lower-case letters, digits and inner hyphens, for example zion-build-release-dp.`;
}

/**
 * The problem of the Display name field, or `null`: the bound sentence for a trimmed name of more
 * than 200 characters. Pure.
 */
export function principalNameProblem(displayName: string): string | null {
  return displayName.trim().length > CP_MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH
    ? `Shorten the display name to ${CP_MACHINE_PRINCIPAL_DISPLAY_NAME_MAX_LENGTH} characters or fewer.`
    : null;
}

/**
 * Why Create of the principal dialog is off, or `null` when it is on. It names the empty fields
 * first (`Still needed: slug, display name.`), then a field with a problem (`Correct the slug.`).
 * Pure.
 */
export function principalBlockedReason(draft: PrincipalDraft): string | null {
  const missing: string[] = [];
  if (draft.slug.trim() === '') missing.push('slug');
  if (draft.displayName.trim() === '') missing.push('display name');
  if (missing.length > 0) return `Still needed: ${missing.join(', ')}.`;
  if (principalSlugProblem(draft.slug) !== null) return 'Correct the slug.';
  if (principalNameProblem(draft.displayName) !== null) return 'Shorten the display name.';
  return null;
}

/** The create body of a draft: both fields trimmed. The caller checks the draft first. Pure. */
export function principalBody(draft: PrincipalDraft): CreateCpMachinePrincipalRequest {
  return { slug: draft.slug.trim(), displayName: draft.displayName.trim() };
}

// ── Trust policy create ──────────────────────────────────────────────────────────────────────────

/** One required claim row of the trust policy dialog, as typed. */
export interface ClaimRow {
  readonly key: string;
  readonly value: string;
}

/** The fields of the trust policy dialog, as the operator set them. */
export interface TrustPolicyDraft {
  readonly issuer: string;
  readonly subject: string;
  readonly claims: readonly ClaimRow[];
  readonly releaseSourceScoped: boolean;
  /** The empty string means no ref rule: the body then carries no `requiredRefPrefix`. */
  readonly requiredRefPrefix: string;
}

/** The draft the trust policy dialog opens with: empty fields and one empty claim row. */
export const EMPTY_TRUST_POLICY_DRAFT: TrustPolicyDraft = {
  issuer: '',
  subject: '',
  claims: [{ key: '', value: '' }],
  releaseSourceScoped: false,
  requiredRefPrefix: '',
};

/** The most claim rows the dialog offers. The JSON bound of the contract holds far fewer real claims. */
export const CLAIM_ROWS_MAX = 12;

/** The problem of the Issuer field, or `null` for an empty or a valid issuer. Pure. */
export function issuerProblem(issuer: string): string | null {
  const value = issuer.trim();
  if (value === '' || cpMachineTrustPolicyIssuerSchema.safeParse(value).success) return null;
  return 'Enter an https URL with a lower-case host and no trailing slash, for example https://token.actions.githubusercontent.com.';
}

/** The problem of the Subject field, or `null` for an empty or a valid subject. Pure. */
export function subjectProblem(subject: string): string | null {
  const value = subject.trim();
  if (value === '' || cpMachineTrustPolicySubjectSchema.safeParse(value).success) return null;
  return 'Use 1 to 512 characters with no *, %, space or control character. The exchange compares the subject exactly.';
}

/** The problem of the Ref prefix field, or `null` for an empty or a valid prefix. Pure. */
export function refPrefixProblem(prefix: string): string | null {
  const value = prefix.trim();
  if (value === '' || cpMachineTrustPolicyRefPrefixSchema.safeParse(value).success) return null;
  return `Enter refs/ and whole segments that each end with /, for example refs/heads/release/. At most ${CP_MACHINE_TRUST_POLICY_REF_PREFIX_MAX_LENGTH} characters.`;
}

/*
 * The claim rows with a key or a value, as typed. A row whose key and value are both empty or
 * blank is a spare row. The exchange compares a claim exactly, so no text of a filled row is
 * trimmed: `claimsProblem` refuses a space at either end instead.
 */
function filledClaimRows(rows: readonly ClaimRow[]): readonly ClaimRow[] {
  return rows.filter((row) => row.key.trim() !== '' || row.value.trim() !== '');
}

/* `true` when `text` has white space at its start or its end. */
function hasEdgeSpace(text: string): boolean {
  return text !== text.trim();
}

/**
 * The problem of the claim rows, or `null`. Pure. In order:
 * - a row with a key and no value, or a value and no key;
 * - a key or a value with white space at either end: the exchange compares claims exactly, so the
 *   console sends the text as typed and asks for the space to go;
 * - a key on two rows;
 * - claims that `cpMachineTrustPolicyRequiredClaimsSchema` refuses (the JSON bound);
 * - a claim name that the schema drops from its answer ({@link droppedClaimProblem}).
 * No filled row is no problem here: the blocked reason names it.
 */
export function claimsProblem(rows: readonly ClaimRow[]): string | null {
  const filled = filledClaimRows(rows);
  if (filled.length === 0) return null;
  if (filled.some((row) => row.key.trim() === '' || row.value.trim() === '')) return 'Each required claim needs a name and a value.';
  if (filled.some((row) => hasEdgeSpace(row.key) || hasEdgeSpace(row.value))) {
    return 'Remove the space at the start or the end of a claim name or value. The exchange compares each claim exactly.';
  }
  const seen = new Set<string>();
  for (const row of filled) {
    if (seen.has(row.key)) return `The claim ${row.key} appears on two rows.`;
    seen.add(row.key);
  }
  const parsed = cpMachineTrustPolicyRequiredClaimsSchema.safeParse(claimsRecord(filled));
  if (!parsed.success) return 'The required claims are too long: their JSON text must be at most 1024 characters.';
  const dropped = filled.find((row) => !Object.hasOwn(parsed.data, row.key));
  return dropped === undefined ? null : droppedClaimProblem(dropped.key);
}

/**
 * The problem of a claim name that the contract schema drops in place of a refusal: `__proto__`,
 * which zod 4 leaves out of a parsed record. The console would then send a policy without that
 * condition, so the dialog refuses the name. Pure.
 */
export function droppedClaimProblem(key: string): string {
  return `The claim name ${key} cannot be sent: the control plane would store the policy without it. Use another claim name.`;
}

/* The claims of filled rows as a record. `Object.fromEntries` gives a plain object with own keys. */
function claimsRecord(rows: readonly ClaimRow[]): Record<string, string> {
  return Object.fromEntries(rows.map((row) => [row.key, row.value]));
}

/**
 * The create body of a draft: the issuer, the subject and the prefix trimmed, the claims as typed,
 * the spare rows dropped, an empty prefix absent. The caller checks the draft first. Pure.
 */
export function trustPolicyBody(draft: TrustPolicyDraft): CreateCpMachineTrustPolicyRequest {
  const prefix = draft.requiredRefPrefix.trim();
  return {
    issuer: draft.issuer.trim(),
    subject: draft.subject.trim(),
    requiredClaims: claimsRecord(filledClaimRows(draft.claims)),
    ...(draft.releaseSourceScoped ? { releaseSourceScoped: true } : {}),
    ...(prefix === '' ? {} : { requiredRefPrefix: prefix }),
  };
}

/** The sentence for the GitHub rule of the contract: a GitHub Actions issuer needs `job_workflow_ref`. */
export const GITHUB_CLAIM_REASON =
  'A GitHub Actions issuer must require the claim job_workflow_ref, the workflow file and ref of the job.';

/** The sentence for the scoped rule of the contract: a scoped policy needs `submitter_kind`. */
export const SCOPED_CLAIM_REASON = 'A release-source scoped policy must require submitter_kind = human or agent.';

/**
 * The sentence for the console rule on a site grant carrier: an unscoped policy with a ref prefix
 * must require `submitter_kind = human`. R-042 item 1 gives a release job its power only for a run
 * that a person submitted, and `CpTokenMinter` puts the site grants into the token of such a policy
 * alone (`carriesSitePermissions`).
 */
export const HUMAN_SUBMITTER_REASON =
  'A policy with a ref prefix gives its tokens the site grants, so it must require submitter_kind = human: a person submitted the run.';

/**
 * `true` when the body must require `submitter_kind = human`: an unscoped policy with a ref prefix,
 * whose tokens carry the site grants. The contract admits such a body with no `submitter_kind`, and
 * the matcher then admits a run that an agent or zion-build itself submitted, so the console refuses
 * more than the contract (F3). Pure.
 */
export function needsHumanSubmitter(body: CreateCpMachineTrustPolicyRequest): boolean {
  if (body.releaseSourceScoped === true || body.requiredRefPrefix === undefined) return false;
  return !(Object.hasOwn(body.requiredClaims, SUBMITTER_KIND_CLAIM) && body.requiredClaims[SUBMITTER_KIND_CLAIM] === 'human');
}

/* The claim of a zion-build job token that names the job: `YAML_JOB_ID_CLAIM` of cp-auth-api (D32). */
const JOB_CLAIM = 'yaml_job_id';

/**
 * The sentence for the console rule on the job claim of a site grant carrier: an unscoped policy
 * with a ref prefix must require `yaml_job_id`. R-042 item 1 gives each job of `release.yml` its own
 * machine identity, and every job token of one workflow run carries the same issuer, subject, ref
 * and submitter. Without the job claim, the token of any other job of the workflow matches the
 * policy and takes this identity's site grants.
 */
export const JOB_CLAIM_REASON =
  'A policy with a ref prefix gives its tokens the site grants, so it must require yaml_job_id: the one job that may take them.';

/**
 * `true` when the body must require {@link JOB_CLAIM}: an unscoped policy with a ref prefix, whose
 * tokens carry the site grants, with no `yaml_job_id` claim. Neither the contract nor cp-auth-api
 * refuses such a body for a principal other than the release principal, so the console refuses more
 * than both (F3). Pure.
 */
export function needsJobClaim(body: CreateCpMachineTrustPolicyRequest): boolean {
  if (body.releaseSourceScoped === true || body.requiredRefPrefix === undefined) return false;
  return !Object.hasOwn(body.requiredClaims, JOB_CLAIM);
}

/**
 * Why Add policy is off, or `null` when it is on. Pure. In order:
 * 1. the empty parts: `Still needed: issuer, subject, a required claim.`, only the empty ones;
 * 2. a field with a problem: `Correct the issuer.` (then subject, claims, ref prefix);
 * 3. a refusal of `createCpMachineTrustPolicyRequestSchema` that no field shows: the scoped rule
 *    ({@link SCOPED_CLAIM_REASON}) when the body passes with the scope off, else the GitHub rule
 *    ({@link GITHUB_CLAIM_REASON}). The schema stays the one authority on both rules;
 * 4. the console rule of {@link needsHumanSubmitter}: {@link HUMAN_SUBMITTER_REASON};
 * 5. the console rule of {@link needsJobClaim}: {@link JOB_CLAIM_REASON}.
 */
export function trustPolicyBlockedReason(draft: TrustPolicyDraft): string | null {
  const missing: string[] = [];
  if (draft.issuer.trim() === '') missing.push('issuer');
  if (draft.subject.trim() === '') missing.push('subject');
  if (filledClaimRows(draft.claims).length === 0) missing.push('a required claim');
  if (missing.length > 0) return `Still needed: ${missing.join(', ')}.`;
  if (issuerProblem(draft.issuer) !== null) return 'Correct the issuer.';
  if (subjectProblem(draft.subject) !== null) return 'Correct the subject.';
  if (claimsProblem(draft.claims) !== null) return 'Correct the required claims.';
  if (refPrefixProblem(draft.requiredRefPrefix) !== null) return 'Correct the ref prefix.';
  const body = trustPolicyBody(draft);
  if (!createCpMachineTrustPolicyRequestSchema.safeParse(body).success) {
    const unscoped = { ...body, releaseSourceScoped: false };
    return createCpMachineTrustPolicyRequestSchema.safeParse(unscoped).success ? SCOPED_CLAIM_REASON : GITHUB_CLAIM_REASON;
  }
  if (needsHumanSubmitter(body)) return HUMAN_SUBMITTER_REASON;
  return needsJobClaim(body) ? JOB_CLAIM_REASON : null;
}

/*
 * A claim key or value that a reader can tell apart in `key = value`: text with no `=`, no `"`, no
 * backslash and no white space at either end.
 */
const PLAIN_CLAIM_TEXT = /^(?!\s)[^="\\]*(?<!\s)$/;

/* One side of a claim line: the text as it stands when plain, else its JSON string form in quotes. */
function claimPart(text: string): string {
  return PLAIN_CLAIM_TEXT.test(text) ? text : JSON.stringify(text);
}

/**
 * The lines of a Required claims cell: `key = value`, one per claim, in code-unit order of the key.
 * A key or a value that holds `=`, `"` or a backslash, or that starts or ends with white space,
 * shows as a JSON string in quotes, so two different claim sets never show the same lines and two
 * claims of one set never show one line. Pure.
 */
export function claimLines(claims: Readonly<Record<string, string>>): readonly string[] {
  return Object.entries(claims)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, value]) => `${claimPart(key)} = ${claimPart(value)}`);
}

/** The Ref rule cell of a policy with no prefix: its tokens carry no site grant. */
export const NO_REF_RULE_LABEL = 'None: no site grant';

/**
 * The Ref rule cell: the prefix through {@link displayMachineText}, or {@link NO_REF_RULE_LABEL} for
 * a policy with none. The minter gives a token of a policy with no prefix no site permission. The
 * prefix comes from the answer, and the contract admits a credential in a path segment, so the cell
 * shows it redacted. Pure.
 */
export function refRuleLabel(prefix: string | null): string {
  return prefix === null ? NO_REF_RULE_LABEL : displayMachineText(prefix);
}

/** The Match cell: what the exchange compares. Pure. */
export function policyMatchLabel(releaseSourceScoped: boolean): string {
  return releaseSourceScoped ? 'Release source, subject not compared' : 'Exact subject';
}

/*
 * A whole number of seconds as words: the hours, the minutes and the seconds that are not zero, each
 * with its unit, such as `1 hour`, `15 minutes` or `1 minute 30 seconds`. The contract bounds a
 * token lifetime to 60 to 3,600 seconds, so the text is never empty. Pure.
 */
function durationWords(totalSeconds: number): string {
  const parts: readonly (readonly [number, string])[] = [
    [Math.floor(totalSeconds / 3600), 'hour'],
    [Math.floor((totalSeconds % 3600) / 60), 'minute'],
    [totalSeconds % 60, 'second'],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([count, unit]) => `${count} ${unit}${count === 1 ? '' : 's'}`)
    .join(' ');
}

/**
 * The Token lifetime cell of a trust policy row (plan 2026-10-31-door-long-push, WS-6): the
 * longest life of a token that the policy mints, as `At most <duration>`. The CP never mints past
 * the `run_deadline` claim of the assertion (D2), so a token can live less.
 *
 * - a `tokenTtlSeconds` value: that value, such as `At most 1 hour` for 3600;
 * - null on an unscoped policy: `At most 15 minutes`, the bound of `CP_FEDERATED_TOKEN_TTL`
 *   (`FEDERATED_TOKEN_MAX_TTL_SECONDS`);
 * - null on a release-source scoped policy: `At most 1 hour`, the bound of
 *   `CP_FEDERATED_RELEASE_TOKEN_TTL` (`FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS`). The contract
 *   refuses a value on a scoped policy (D3), so its own setting bounds its tokens.
 *
 * Callers: the Trust policies card of `MachineIdentityPane.tsx`. Input: a policy view, whose
 * `tokenTtlSeconds` the answer schema bounds to 60 to 3,600 or null. Output: one line. Pure.
 */
export function tokenLifetimeLabel(policy: Pick<CpMachineTrustPolicy, 'tokenTtlSeconds' | 'releaseSourceScoped'>): string {
  const bound = policy.releaseSourceScoped ? FEDERATED_RELEASE_TOKEN_MAX_TTL_SECONDS : FEDERATED_TOKEN_MAX_TTL_SECONDS;
  return `At most ${durationWords(policy.tokenTtlSeconds ?? bound)}`;
}

// ── Site grants ──────────────────────────────────────────────────────────────────────────────────

/**
 * What each site permission opens for a machine (R-036, R-037, R-044). The SDK registry doors read
 * `cp:platform:publish` from the site permissions of the release principal alone; every other
 * machine needs the `platform_publisher` resource grant (WS-6b, R-044 item 3). Total over
 * `CpMachineSitePermission`: a new member of the closed list is a compile error here.
 */
export const SITE_PERMISSION_PURPOSE: Readonly<Record<CpMachineSitePermission, string>> = {
  'cp:alert:silence': 'Create and delete alert silences during a release.',
  'cp:deployment-config:converge': 'Write the deployment config of the control plane, as release-cp does.',
  'cp:deployment-config:read': 'Read the deployment config of the control plane, as release-cp does.',
  'cp:platform-channel:pin': 'Pin a platform version on a platform channel.',
  'cp:platform-channel:promote-version': 'Register, promote and deprecate platform versions.',
  'cp:platform-channel:read': 'Read the platform channels and their versions.',
  'cp:platform:publish':
    'The SDK registry doors read this site permission from the release principal zion-build-platform-release alone, until the release after the cutover. Any other machine identity publishes there through the platform_publisher resource grant on the platform.',
};

/** The purpose sentence of one site permission. Pure. */
export function sitePermissionPurpose(permission: CpMachineSitePermission): string {
  return SITE_PERMISSION_PURPOSE[permission];
}

/**
 * The site permissions the principal does not hold yet, in the order of
 * `CP_MACHINE_SITE_PERMISSIONS`. These are the choices of the add dialog. Pure.
 */
export function sitePermissionsToAdd(held: readonly CpMachineSiteGrant[]): readonly CpMachineSitePermission[] {
  const have = new Set<string>(held.map((grant) => grant.permission));
  return CP_MACHINE_SITE_PERMISSIONS.filter((permission) => !have.has(permission));
}

/**
 * Why Add site grant holds, or `null` when it may open. Pure.
 * - a disabled principal: {@link SITE_GRANT_DISABLED_REASON};
 * - no permission left to add: {@link SITE_GRANT_COMPLETE_REASON}.
 */
export function siteGrantHoldReason(disabled: boolean, held: readonly CpMachineSiteGrant[]): string | null {
  if (disabled) return SITE_GRANT_DISABLED_REASON;
  return sitePermissionsToAdd(held).length === 0 ? SITE_GRANT_COMPLETE_REASON : null;
}

// ── Resource grants ──────────────────────────────────────────────────────────────────────────────

/** The visible name of each resource type. Total over `GrantResourceType`. */
export const RESOURCE_TYPE_LABEL: Readonly<Record<GrantResourceType, string>> = {
  app: 'App',
  tenant: 'Tenant',
  dp: 'Data plane',
  platform: 'Platform',
};

/**
 * The resource types the add dialog offers, in the order of `GRANT_RESOURCE_TYPES`. `platform` is
 * offered only to a holder of `cp:platform:admin`: the route answers 403 to anyone else. Pure.
 */
export function resourceTypesOffered(canGrantPlatform: boolean): readonly GrantResourceType[] {
  return GRANT_RESOURCE_TYPES.filter((type) => canGrantPlatform || type !== 'platform');
}

/**
 * The resource type of a select value: the offered type equal to `value`, else the first offered
 * type (`app`, which every offer holds). Pure.
 */
export function pickResourceType(value: string, offered: readonly GrantResourceType[]): GrantResourceType {
  return offered.find((type) => type === value) ?? 'app';
}

/** The fields of the resource grant dialog, as set. The two text fields are untrimmed. */
export interface ResourceGrantDraft {
  readonly resourceType: GrantResourceType;
  readonly resourceId: string;
  readonly roleSlug: string;
}

/** The draft the dialog opens with: an app grant with two empty text fields. */
export const EMPTY_RESOURCE_GRANT_DRAFT: ResourceGrantDraft = { resourceType: 'app', resourceId: '', roleSlug: '' };

/**
 * The resource id the body carries: `PLATFORM_RESOURCE_ID` for a `platform` grant, whatever was
 * typed, else the typed id trimmed. The route answers 400 for a `platform` grant on any other id.
 * Pure.
 */
export function grantResourceId(draft: ResourceGrantDraft): string {
  return draft.resourceType === 'platform' ? PLATFORM_RESOURCE_ID : draft.resourceId.trim();
}

/**
 * Why Grant is off, or `null` when it is on. Pure. It names the empty fields
 * (`Still needed: resource id, role.`; a `platform` grant needs no resource id), then a field past
 * its bound (256 for the resource id, 120 for the role).
 */
export function resourceGrantBlockedReason(draft: ResourceGrantDraft): string | null {
  const missing: string[] = [];
  if (grantResourceId(draft) === '') missing.push('resource id');
  if (draft.roleSlug.trim() === '') missing.push('role');
  if (missing.length > 0) return `Still needed: ${missing.join(', ')}.`;
  if (grantResourceId(draft).length > CP_MACHINE_RESOURCE_GRANT_ID_MAX_LENGTH) {
    return `Shorten the resource id to ${CP_MACHINE_RESOURCE_GRANT_ID_MAX_LENGTH} characters or fewer.`;
  }
  if (draft.roleSlug.trim().length > CP_MACHINE_RESOURCE_GRANT_ROLE_SLUG_MAX_LENGTH) {
    return `Shorten the role to ${CP_MACHINE_RESOURCE_GRANT_ROLE_SLUG_MAX_LENGTH} characters or fewer.`;
  }
  return null;
}

/** The grant body of a draft for one machine principal. The caller checks the draft first. Pure. */
export function resourceGrantBody(principalId: string, draft: ResourceGrantDraft): CreateCpMachineResourceGrantRequest {
  return {
    principalType: 'machine',
    principalId,
    resourceType: draft.resourceType,
    resourceId: grantResourceId(draft),
    roleSlug: draft.roleSlug.trim(),
  };
}

/**
 * The Resource cell: `The platform` for a `platform` row on `PLATFORM_RESOURCE_ID`, else
 * `<type label> <id>` with the id through {@link displayMachineText}. A `platform` row on any other
 * id, which the answer schema admits, shows its id, so no malformed row reads as the platform
 * resource. The id comes from the answer and can hold a credential, so it shows redacted. Pure.
 */
export function resourceLabel(row: Pick<CpMachineResourceGrantRow, 'resourceType' | 'resourceId'>): string {
  if (row.resourceType === 'platform' && row.resourceId === PLATFORM_RESOURCE_ID) return 'The platform';
  return `${RESOURCE_TYPE_LABEL[row.resourceType]} ${displayMachineText(row.resourceId)}`;
}

/** `true` while a resource grant is active: `revokedAt` is null. Pure. */
export function isGrantActive(row: Pick<CpMachineResourceGrantRow, 'revokedAt'>): boolean {
  return row.revokedAt === null;
}

// ── Failures and notices ─────────────────────────────────────────────────────────────────────────

/*
 * One value of a refusal body with {@link redactMachineSecrets} on each string: a string, or each
 * string of an array (the strict-validation 400 sends `message` as an array). Any other value stays.
 */
function redactBodyValue(value: unknown): unknown {
  if (typeof value === 'string') return redactMachineSecrets(value);
  return Array.isArray(value) ? value.map((item: unknown) => (typeof item === 'string' ? redactMachineSecrets(item) : item)) : value;
}

/*
 * The cause with {@link redactMachineSecrets} on every string of its body, before `displayMessage`
 * caps and escapes the text: an `ApiError` with an object body becomes a new `ApiError` of the same
 * status. Any other cause stays, because only a body carries server text. Pure.
 */
function redactedCause(cause: unknown): unknown {
  if (!(cause instanceof ApiError) || cause.body === null || typeof cause.body !== 'object') return cause;
  return new ApiError(cause.status, Object.fromEntries(Object.entries(cause.body).map(([key, value]) => [key, redactBodyValue(value)])));
}

/**
 * The failure line of a write: the server's reason, else `fallback` (the `displayMessage` rules).
 * {@link redactMachineSecrets} runs on the strings of the refusal body before `displayMessage` caps
 * and escapes them, and again on its line. `displayMessage` redacts the secret shapes of
 * `displayUntrusted` and keeps a hex session token and a joined hex secret, so this function hides
 * those too, also when a separator splits one or the cap would cut one. Pure.
 *
 * A server error (`ApiError` of status 500 to 599) gives `fallback` and no text of the answer: the
 * text of a server error can come from a proxy or a gateway and carry a credential or a personal
 * detail that `displayUntrusted` keeps. A dialog shows `UNCERTAIN_WRITE_LINE` for a server error
 * (`isUncertainWriteFailure`), so this branch is the second guard on the same text.
 */
export function machineWriteFailure(cause: unknown, fallback: string): string {
  if (cause instanceof ApiError && cause.status >= 500) return fallback;
  return redactMachineSecrets(displayMessage(redactedCause(cause), fallback));
}

/**
 * The sentence a machine identity card shows for a read that failed with a server error, in place
 * of the text of the answer.
 */
export const SERVER_ERROR_READ_LINE =
  'The control plane answered with a server error. The console does not show the text of a server error, because it can hold a credential or a personal detail.';

/**
 * The message an `ErrorState` of the machine identity pages shows for a failed read. Pure.
 *
 * - `status` 500 to 599: {@link SERVER_ERROR_READ_LINE}. The text of a server error can come from a
 *   proxy or a gateway, and `displayUntrusted` keeps an email address or a hex token in it.
 * - Any other status, or no status (a network fault or a contract error of this console): `message`,
 *   the refusal sentence of cp-auth-api or of the console, through {@link displayMachineText}. The
 *   `ErrorState` redacts the secret shapes of `displayUntrusted` again, and keeps a hex session token
 *   and a joined hex secret, so this function hides those first. `useApiResource` escaped the
 *   message already, and {@link redactMachineSecrets} reads an escape token as a separator, so a
 *   hex run that U+200B split goes whole.
 */
export function machineReadFailure(failure: { readonly status: number | null; readonly message: string }): string {
  return failure.status !== null && failure.status >= 500 ? SERVER_ERROR_READ_LINE : displayMachineText(failure.message);
}

/**
 * The status line after each write. Each names the subject and the act. A caller passes each name
 * through {@link displayMachineName} or {@link displayMachineText} first; a site permission is a
 * member of the closed list and passes as it is.
 */
export const machineNotices = {
  created: (name: string): string => `Created ${name}.`,
  disabled: (name: string): string => `Disabled ${name}.`,
  policyAdded: (issuer: string): string => `Added a trust policy for ${issuer}.`,
  policyDisabled: (issuer: string): string => `Disabled the trust policy for ${issuer}.`,
  siteGranted: (permission: string): string => `Granted ${permission}.`,
  siteRemoved: (permission: string): string => `Removed ${permission}.`,
  resourceGranted: (roleSlug: string, resource: string): string => `Granted ${roleSlug} on ${resource}.`,
  resourceRevoked: (roleSlug: string, resource: string): string => `Revoked ${roleSlug} on ${resource}.`,
} as const;
